import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import clientApi from "@/lib/clientApi";

const ClientAuthContext = createContext(null);

export const ClientAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("eazy_client_token");
    if (!token) {
      setChecking(false);
      return;
    }
    clientApi
      .get("/client/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("eazy_client_token");
        setUser(null);
      })
      .finally(() => setChecking(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await clientApi.post("/client/login", { email, password });
    localStorage.setItem("eazy_client_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (payload) => {
    const { data } = await clientApi.post("/client/signup", payload);
    localStorage.setItem("eazy_client_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("eazy_client_token");
    setUser(null);
  }, []);

  const updateUser = useCallback((partial) => {
    setUser((u) => (u ? { ...u, ...partial } : u));
  }, []);

  const value = useMemo(
    () => ({ user, checking, login, signup, logout, updateUser }),
    [user, checking, login, signup, logout, updateUser]
  );

  return <ClientAuthContext.Provider value={value}>{children}</ClientAuthContext.Provider>;
};

export const useClientAuth = () => {
  const ctx = useContext(ClientAuthContext);
  if (!ctx) throw new Error("useClientAuth must be used inside ClientAuthProvider");
  return ctx;
};
